import { create } from "zustand";
import { useFormEditorStore } from "./form-editor-store";

export type EntityTypeFormMode = "preview" | "build" | "rules";

interface EntityTypeFormModeState {
	modeByTypeId: Record<string, EntityTypeFormMode>;
	setMode: (typeId: string, mode: EntityTypeFormMode) => void;
	clearMode: (typeId: string) => void;
}

export const useEntityTypeFormModeStore = create<EntityTypeFormModeState>(
	(set) => ({
		modeByTypeId: {},
		setMode: (typeId, mode) =>
			set((state) => ({
				modeByTypeId: { ...state.modeByTypeId, [typeId]: mode },
			})),
		clearMode: (typeId) =>
			set((state) => {
				if (!(typeId in state.modeByTypeId)) return state;
				const modeByTypeId = { ...state.modeByTypeId };
				delete modeByTypeId[typeId];
				return { modeByTypeId };
			}),
	}),
);

export function useEntityTypeFormMode(typeId: string): EntityTypeFormMode {
	return useEntityTypeFormModeStore(
		(state) => state.modeByTypeId[typeId] ?? "preview",
	);
}

/** Remember which form identity is already in the editor store so a remount of the same type does not wipe unsaved edits. */
let appliedHydrationKey: string | null = null;

export function shouldSkipFormHydration(hydrationKey: string): boolean {
	return appliedHydrationKey === hydrationKey;
}

export function rememberFormHydration(hydrationKey: string): void {
	appliedHydrationKey = hydrationKey;
}

let sharedBaselineKey: string | null = null;
let sharedBaseline: string | null = null;

export function rememberFormBaseline(
	hydrationKey: string,
	baseline: string,
): void {
	sharedBaselineKey = hydrationKey;
	sharedBaseline = baseline;
}

export function readFormBaseline(hydrationKey: string): string | null {
	if (sharedBaselineKey !== hydrationKey) return null;
	return sharedBaseline;
}

/** Type currently holding the editor store. A remount of the same key keeps the draft. */
let activeFormEditorTypeKey: string | null = null;

/**
 * Returns true when this type should reset the editor store.
 * A second mount of the same type, including drawer to page, returns false.
 */
export function claimFormEditorType(typeKey: string): boolean {
	if (activeFormEditorTypeKey === typeKey) return false;
	activeFormEditorTypeKey = typeKey;
	appliedHydrationKey = null;
	sharedBaselineKey = null;
	sharedBaseline = null;
	return true;
}

let releaseTypeKey: string | null = null;
let releaseQueued = false;

export function cancelFormEditorRelease(typeKey: string): void {
	if (releaseTypeKey === typeKey) releaseTypeKey = null;
}

/**
 * Clears the mode and editor draft after the current turn unless the same type
 * mounts again first. Drawer and page can swap without dropping unsaved edits.
 */
export function scheduleFormEditorRelease(typeKey: string): void {
	releaseTypeKey = typeKey;
	if (releaseQueued) return;
	releaseQueued = true;
	queueMicrotask(() => {
		releaseQueued = false;
		const key = releaseTypeKey;
		releaseTypeKey = null;
		if (key == null || activeFormEditorTypeKey !== key) return;
		activeFormEditorTypeKey = null;
		appliedHydrationKey = null;
		sharedBaselineKey = null;
		sharedBaseline = null;
		const separator = key.indexOf(":");
		const typeId = separator === -1 ? key : key.slice(separator + 1);
		useEntityTypeFormModeStore.getState().clearMode(typeId);
		useFormEditorStore.getState().reset();
	});
}
